/**
 * Tab Nghiên cứu pháp lý — nội dung tra cứu QPPPL & án lệ (1.8)
 */
import { useState } from 'react'
import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { Search, ExpandMore, MenuBook, Gavel, Close, Send, Chat as ChatIcon, SmartToy } from '@mui/icons-material'

const LAW_SECTIONS = [
  { title: 'Tố tụng dân sự', items: ['BLTTDS 2015 (Điều 203)', 'BLTTDS 2015 (Điều 227)', 'BLTTDS 2015 (Điều 147)'] },
  { title: 'Hôn nhân & gia đình', items: ['Luật HN&GĐ 2014 (Điều 33)', 'Luật HN&GĐ 2014 (Điều 59)', 'Luật HN&GĐ 2014 (Điều 43)'] },
  { title: 'Dân sự & hợp đồng', items: ['BLDS 2015 (Điều 351)', 'BLDS 2015 (Điều 418)', 'BLDS 2015 (Điều 429)'] },
  { title: 'Án lệ & bản án tham khảo', items: ['Án lệ số 08/2019/AL', 'Án lệ số 03/2016/AL', 'Án lệ số 09/2018/AL'] },
]

const ALL_LAW_RESULTS = [
  {
    id: '1',
    title: 'Luật HN&GĐ 2014 (Điều 33) – Tài sản chung của vợ chồng',
    excerpt: 'Xác định tài sản hình thành trong thời kỳ hôn nhân là tài sản chung, trừ trường hợp được chứng minh là tài sản riêng.',
    full: 'Điều 33 (trích). Tài sản chung của vợ chồng bao gồm tài sản do vợ, chồng tạo ra, thu nhập do lao động, hoạt động sản xuất, kinh doanh... trong thời kỳ hôn nhân.',
    category: 'hngd',
  },
  {
    id: '2',
    title: 'Luật HN&GĐ 2014 (Điều 59) – Nguyên tắc giải quyết tài sản khi ly hôn',
    excerpt: 'Nguyên tắc chia tài sản: có tính đến công sức đóng góp, lỗi, hoàn cảnh, bảo vệ quyền lợi chính đáng của các bên.',
    full: 'Điều 59 (trích). Việc chia tài sản chung do các bên thỏa thuận; nếu không thỏa thuận được thì Tòa án giải quyết theo quy định...',
    category: 'hngd',
  },
  {
    id: '3',
    title: 'BLDS 2015 (Điều 418) – Phạt vi phạm',
    excerpt: 'Quy định về phạt vi phạm trong hợp đồng; căn cứ xem xét tính hợp lý của mức phạt và thỏa thuận giữa các bên.',
    full: 'Điều 418 (trích). Phạt vi phạm là sự thỏa thuận giữa các bên trong hợp đồng, theo đó bên vi phạm nghĩa vụ phải nộp một khoản tiền cho bên bị vi phạm...',
    category: 'blds',
  },
  {
    id: '4',
    title: 'BLTTDS 2015 (Điều 227) – Sự có mặt của đương sự',
    excerpt: 'Xử lý trường hợp đương sự vắng mặt tại phiên tòa; căn cứ để xét xử vắng mặt theo điều kiện luật định.',
    full: 'Điều 227 (trích). Tòa án triệu tập hợp lệ lần thứ nhất... nếu đương sự vắng mặt thì hoãn phiên tòa; lần thứ hai có thể xét xử vắng mặt theo quy định...',
    category: 'blttds',
  },
  {
    id: '5',
    title: 'BLDS 2015 (Điều 429) – Thời hiệu khởi kiện',
    excerpt: 'Xác định thời hiệu khởi kiện đối với tranh chấp hợp đồng; lưu ý mốc thời gian phát sinh quyền khởi kiện.',
    full: 'Điều 429 (trích). Thời hiệu khởi kiện để yêu cầu Tòa án giải quyết tranh chấp hợp đồng là 03 năm, kể từ ngày người có quyền yêu cầu biết hoặc phải biết quyền lợi bị xâm phạm.',
    category: 'blds',
  },
]

const ALL_CASE_LAW = [
  {
    id: '1',
    code: 'Án lệ số 08/2019/AL',
    summary: 'Tiêu chí xác định tài sản riêng/tài sản chung và nghĩa vụ chứng minh nguồn gốc tài sản khi có tranh chấp.',
    result: 'Tòa đánh giá chứng cứ về nguồn tiền, thời điểm hình thành tài sản và công sức đóng góp.',
    full: 'Tóm tắt nội dung án lệ (minh họa): Tòa án xem xét sao kê, hợp đồng mua bán, thời điểm đăng ký... để kết luận tính chất tài sản.',
  },
  {
    id: '2',
    code: 'Án lệ số 03/2016/AL',
    summary: 'Nguyên tắc giải quyết quyền nuôi con và nghĩa vụ cấp dưỡng khi ly hôn.',
    result: 'Ưu tiên lợi ích tốt nhất của trẻ; xem xét điều kiện chăm sóc, môi trường sống.',
    full: 'Tóm tắt nội dung án lệ (minh họa).',
  },
  {
    id: '3',
    code: 'Bản án tham khảo (dạng demo)',
    summary: 'Mẫu nhận định về tranh chấp hợp đồng: hiệu lực điều khoản, phạt vi phạm, và đánh giá hành vi vi phạm nghĩa vụ.',
    result: 'Tòa chấp nhận một phần yêu cầu; điều chỉnh mức phạt vi phạm theo thỏa thuận và chứng cứ.',
    full: 'Nội dung bản án tham khảo (minh họa).',
  },
]

export function LegalResearchTab() {
  const theme = useTheme()
  const [chatOpen, setChatOpen] = useState(false)
  const [query, setQuery] = useState('Tranh chấp tài sản chung sau ly hôn; yêu cầu xác định tài sản riêng/tài sản chung')
  const [searchDone, setSearchDone] = useState(true)
  const [selectedLawItem, setSelectedLawItem] = useState<string | null>(null)
  const [lawResults] = useState(ALL_LAW_RESULTS)
  const [caseResults] = useState(ALL_CASE_LAW)
  const [viewLawId, setViewLawId] = useState<string | null>(null)
  const [viewCaseId, setViewCaseId] = useState<string | null>(null)
  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'bot'; text: string }[]>([
    { role: 'bot', text: 'Tôi đã tổng hợp các điều luật/án lệ liên quan đến tài sản chung, nghĩa vụ chứng minh và phạt vi phạm. Bạn muốn ưu tiên phần nào?' },
  ])
  const [chatInput, setChatInput] = useState('')

  const isCaseLawSelection = selectedLawItem?.includes('Án lệ') ?? false
  const displayedLawResults =
    !selectedLawItem || isCaseLawSelection
      ? lawResults
      : lawResults.filter((r) => r.title.includes(selectedLawItem) || selectedLawItem.includes(r.title))
  const displayedCaseResults =
    !selectedLawItem
      ? caseResults
      : isCaseLawSelection
        ? caseResults.filter((c) => c.code === selectedLawItem)
        : caseResults

  const handleSearch = () => {
    setSearchDone(false)
    setTimeout(() => {
      setSearchDone(true)
    }, 800)
  }

  const handleSendChat = () => {
    if (!chatInput.trim()) return
    setChatMessages((prev) => [...prev, { role: 'user', text: chatInput }])
    setChatInput('')
    setTimeout(() => {
      setChatMessages((prev) => [
        ...prev,
        { role: 'bot', text: 'Tôi đã ghi nhận câu hỏi. (Demo: phản hồi AI sẽ hiển thị khi tích hợp backend.)' },
      ])
    }, 500)
  }

  return (
    <>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.75, sm: 2 },
          mb: 2,
          borderRadius: 0,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25, fontWeight: 500 }}>
          Tìm kiếm theo tình huống hoặc từ khóa
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'stretch' }}>
          <TextField
            fullWidth
            placeholder="VD: tranh chấp ly hôn, đất đai, hợp đồng..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            size="medium"
            hiddenLabel
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'primary.main', opacity: 0.85 }} fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.grey[500], 0.06),
                '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.08) },
                '&.Mui-focused': { bgcolor: 'background.paper' },
              },
            }}
          />
          <Button
            variant="contained"
            disableElevation
            onClick={handleSearch}
            disabled={!searchDone}
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: 1.5,
              minWidth: { xs: '100%', sm: 132 },
              flexShrink: 0,
              fontWeight: 600,
              alignSelf: { sm: 'stretch' },
            }}
          >
            Phân tích
          </Button>
        </Stack>
        {!searchDone && <LinearProgress sx={{ mt: 1.5, borderRadius: 1, height: 3 }} />}
      </Paper>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          minHeight: 0,
          borderRadius: 0,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: { xs: 200, sm: 240, md: 268 },
            borderRight: 1,
            borderRadius: 0,
            borderColor: 'divider',
            overflow: 'auto',
            flexShrink: 0,
            bgcolor: alpha(theme.palette.grey[500], 0.04),
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', letterSpacing: 0.6, mb: 1 }}>
              Tham chiếu
            </Typography>
            <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 1.5 }}>
              Kho luật
            </Typography>
            {selectedLawItem && (
              <Button size="small" variant="outlined" fullWidth onClick={() => setSelectedLawItem(null)} sx={{ mb: 1.5, borderRadius: 1.5 }}>
                Bỏ lọc
              </Button>
            )}
            {LAW_SECTIONS.map((sec, i) => {
              const isCaseSection = sec.title.includes('Án lệ')
              return (
                <Accordion
                  key={sec.title}
                  defaultExpanded={i < 2}
                  disableGutters
                  elevation={0}
                  sx={{
                    '&:before': { display: 'none' },
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 0,
                    mb: 1,
                    overflow: 'hidden',
                    '& .MuiAccordionSummary-root': { minHeight: 44, bgcolor: alpha(theme.palette.primary.main, 0.04) },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore fontSize="small" />}>
                    <Typography variant="body2" fontWeight={600}>
                      {sec.title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                    {sec.items.map((item) => (
                      <Box
                        key={item}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                          py: 0.75,
                          px: 1,
                          cursor: 'pointer',
                          borderRadius: 0,
                          bgcolor: selectedLawItem === item ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                          border: 1,
                          borderColor: selectedLawItem === item ? alpha(theme.palette.primary.main, 0.35) : 'transparent',
                          transition: 'background-color 0.15s, border-color 0.15s',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                        }}
                        onClick={() => setSelectedLawItem(selectedLawItem === item ? null : item)}
                      >
                        {isCaseSection ? (
                          <Gavel sx={{ fontSize: 18, color: 'secondary.main', mt: 0.15, flexShrink: 0 }} />
                        ) : (
                          <MenuBook sx={{ fontSize: 18, color: 'primary.main', mt: 0.15, flexShrink: 0 }} />
                        )}
                        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )
            })}
          </Box>
        </Paper>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 0,
            overflow: 'auto',
            minWidth: 0,
            bgcolor: alpha(theme.palette.grey[500], 0.03),
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              p: { xs: 2, sm: 2.25 },
              borderBottom: { xs: 1, lg: 0 },
              borderRight: { xs: 0, lg: 1 },
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pb: 1.25,
                mb: 2,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <MenuBook color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={700} component="h2" sx={{ letterSpacing: 0.02 }}>
                Văn bản Quy phạm Pháp luật liên quan
              </Typography>
            </Box>
            <Stack spacing={2}>
              {displayedLawResults.map((r) => (
                <Card
                  key={r.id}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 1 },
                  }}
                >
                  <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
                    <Typography variant="body2" fontWeight={700} color="primary.dark" sx={{ mb: 0.75 }}>
                      {r.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                      {r.excerpt}
                    </Typography>
                    <Button size="small" variant="contained" sx={{ mt: 1.25, borderRadius: 1.5 }} onClick={() => setViewLawId(r.id)}>
                      Xem toàn bộ
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', lg: 'block' }, borderColor: 'divider' }} />
          <Box sx={{ flex: 1, minWidth: 0, p: { xs: 2, sm: 2.25 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pb: 1.25,
                mb: 2,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Gavel color="secondary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={700} component="h2" sx={{ letterSpacing: 0.02 }}>
                Án lệ & bản án
              </Typography>
            </Box>
            <Stack spacing={2}>
              {displayedCaseResults.map((r) => (
                <Card
                  key={r.id}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 1 },
                  }}
                >
                  <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
                    <Typography variant="body2" fontWeight={700} color="secondary.dark" sx={{ mb: 0.75 }}>
                      {r.code}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                      <Box component="span" fontWeight={600} color="text.primary">
                        Tóm tắt:{' '}
                      </Box>
                      {r.summary}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.65 }}>
                      <Box component="span" fontWeight={600}>
                        Kết quả:{' '}
                      </Box>
                      {r.result}
                    </Typography>
                    <Button size="small" variant="contained" color="secondary" sx={{ mt: 1.25, borderRadius: 1.5 }} onClick={() => setViewCaseId(r.id)}>
                      Xem chi tiết
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
      >
        <Collapse in={chatOpen} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: 380 }}>
            <Paper
              elevation={8}
              sx={{
                width: 380,
                height: 520,
                maxHeight: 'calc(100vh - 0px)',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 0,
                overflow: 'hidden',
                border: 1,
                borderColor: alpha(theme.palette.primary.main, 0.12),
                borderBottom: 'none',
                boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
              }}
            >
              <Box
                sx={{
                  py: 1.5,
                  px: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha('#fff', 0.2), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SmartToy sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Trợ lý AI Research
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Pháp luật & Án lệ
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small" sx={{ color: 'inherit' }} onClick={() => setChatOpen(false)} aria-label="Đóng">
                  <Close />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: alpha(theme.palette.grey[500], 0.04), display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {chatMessages.map((msg, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: '88%',
                    }}
                  >
                    {msg.role === 'bot' ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexDirection: 'row' }}>
                        <Box
                          sx={{
                            mt: 0.5,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: alpha(theme.palette.primary.main, 0.14),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <SmartToy sx={{ fontSize: 16, color: 'primary.main' }} />
                        </Box>
                        <Paper
                          elevation={0}
                          sx={{
                            px: 2,
                            py: 1.5,
                            borderRadius: 2,
                            borderTopLeftRadius: 4,
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: alpha(theme.palette.primary.main, 0.12),
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          }}
                        >
                          <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.primary' }}>
                            {msg.text}
                          </Typography>
                        </Paper>
                      </Box>
                    ) : (
                      <Paper
                        elevation={0}
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderRadius: 2,
                          borderTopRightRadius: 4,
                          bgcolor: theme.palette.primary.main,
                          color: 'primary.contrastText',
                          boxShadow: '0 2px 8px rgba(20, 80, 140, 0.25)',
                        }}
                      >
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                          {msg.text}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                ))}
              </Box>
              <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Hỏi thêm về pháp luật, án lệ..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendChat()}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: alpha(theme.palette.grey[500], 0.06) },
                  }}
                />
                <IconButton
                  onClick={handleSendChat}
                  disabled={!chatInput.trim()}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&.Mui-disabled': { bgcolor: alpha(theme.palette.primary.main, 0.4), color: 'white' },
                  }}
                  aria-label="Gửi"
                >
                  <Send fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          </Box>
        </Collapse>
        {!chatOpen && (
          <IconButton
            onClick={() => setChatOpen(true)}
            sx={{
              width: 56,
              height: 56,
              borderRadius: '16px 16px 0 0',
              bgcolor: 'primary.main',
              color: 'white',
              boxShadow: '0 -2px 12px rgba(20, 80, 140, 0.4)',
              '&:hover': {
                bgcolor: 'primary.dark',
                boxShadow: '0 -4px 16px rgba(20, 80, 140, 0.45)',
              },
            }}
            aria-label="Mở trợ lý AI"
          >
            <ChatIcon />
          </IconButton>
        )}
      </Box>

      <Dialog open={!!viewLawId} onClose={() => setViewLawId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle>Văn bản Quy phạm Pháp luật</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{ALL_LAW_RESULTS.find((r) => r.id === viewLawId)?.full ?? ''}</Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewCaseId} onClose={() => setViewCaseId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle>Án lệ</DialogTitle>
        <DialogContent>
          {(() => {
            const c = ALL_CASE_LAW.find((r) => r.id === viewCaseId)
            return c ? (
              <>
                <Typography variant="body2" fontWeight={600}>
                  {c.code}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {c.summary}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Kết quả: {c.result}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {c.full}
                </Typography>
              </>
            ) : null
          })()}
        </DialogContent>
      </Dialog>
    </>
  )
}
